import { setAuthConfig } from '@distribusion/owl-auth'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import config from '@config'
import { SeatsSettingsContext } from '@context/seats'
import UISettingsContextProvider from '@context/UISettings'
import { useMoovitBackToWallet, useMoovitIntegrationEnabled } from '@hooks/useMoovitIntegration'
import resolve from '@lib/resolve'

interface AppContext {
  retailerPartnerNumber: RetailerNumber
  marketingCarrierCode: CarrierCode | null
}

const AppContext = createContext<AppContext>({
  retailerPartnerNumber: 0,
  marketingCarrierCode: null,
})

type AppContextProviderProps = {
  children: React.ReactNode
  retailerPartnerNumber: RetailerNumber
  marketingCarrierCode?: CarrierCode | null
  handleLogoClick?: () => void
}

export const AppContextProvider = ({ children, handleLogoClick, ...value }: AppContextProviderProps) => {
  const context = {
    retailerPartnerNumber: value.retailerPartnerNumber,
    marketingCarrierCode: value.marketingCarrierCode ?? null,
  }
  return (
    <AppContext.Provider value={context}>
      <UISettingsContextProvider>
        <SeatsSettingsContext handleLogoClick={handleLogoClick}>{children}</SeatsSettingsContext>
      </UISettingsContextProvider>
    </AppContext.Provider>
  )
}

export const URLAppContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchParams] = useSearchParams()
  const moovitEnabled = useMoovitIntegrationEnabled()
  const handleLogoClick = useMoovitBackToWallet()

  const rawRetailerPartnerNumber = searchParams.get('retailerPartnerNumber') ?? null
  const marketingCarrierCode = searchParams.get('marketingCarrierCode') ?? null

  const retailerPartnerNumber = useMemo(() => {
    const rpn = Number(rawRetailerPartnerNumber)

    if (rpn > 0) return rpn

    return resolve.retailerPartnerNumber() ?? 0
  }, [rawRetailerPartnerNumber])

  const context = { retailerPartnerNumber, marketingCarrierCode }

  useEffect(() => {
    setAuthConfig({
      baseUrl: config.auth.baseUrl,
      retailerPartnerNumber: String(retailerPartnerNumber),
    })
  }, [retailerPartnerNumber])

  return (
    <AppContextProvider handleLogoClick={moovitEnabled ? handleLogoClick : undefined} {...context}>
      {children}
    </AppContextProvider>
  )
}

export const useAppContext = () => useContext(AppContext)
